import React from 'react';
import { i18n } from 'config/locales';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import DateControl from 'components/Form/DateControl';
import Checkbox from 'components/Form/Checkbox';
import Textarea from 'components/Form/Textarea';
import Currency from 'components/Form/Currency';
import Radio from 'components/Form/Radio';
import RadioGroup from 'components/Form/RadioGroup';
import CheckboxGroup from 'components/Form/CheckboxGroup';
import { federalDebtTypes, nonTaxDebtActionOptions, taxDebtActionOptions } from 'constants/enums/financialOptions';
import { FinancialFederalDebtItemModel } from 'forms/sections/financial/federaldebt/types';
import NotApplicable from 'components/Form/NotApplicable';
import Branch from 'components/Form/Branch';
import Text from 'components/Form/Text';
import Layouts from 'components/Form/PVQLocation/Layouts';
import PVQLocation from 'components/Form/PVQLocation';
import { FieldModel } from 'forms/shared/types';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonScrollIntoView,
  CommonRequired,
  Attribute,
} from '../../../../types/props';
import { ValidationElementProps } from '../../../Form/ValidationElement/ValidationElement';

interface Props extends Partial<FinancialFederalDebtItemModel>, ValidationElementProps {
  attribute?: Attribute
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  activity?: string
}

export default class FederalDebtItem extends ValidationElement<Props> {
  update = (queue: Partial<FinancialFederalDebtItemModel>) => {
    this.props.onUpdate({
      FederalDebtType: this.props.FederalDebtType,
      FederalDebtTypeReason: this.props.FederalDebtTypeReason,
      Year: this.props.Year,
      Reason: this.props.Reason,
      ReasonNoActions: this.props.ReasonNoActions,
      Intent: this.props.Intent,
      Date: this.props.Date,
      Amount: this.props.Amount,
      AmountIDK: this.props.AmountIDK,
      AmountEstimated: this.props.AmountEstimated,
      JudgementAmount: this.props.JudgementAmount,
      ActionsTaken: this.props.ActionsTaken,
      ActionsTakenReason: this.props.ActionsTakenReason,
      Agency: this.props.Agency,
      CourtName: this.props.CourtName,
      CourtLocation: this.props.CourtLocation,
      AccountNum: this.props.AccountNum,
      PropertyType: this.props.PropertyType,
      PropertyTypeNotApplicable: this.props.PropertyTypeNotApplicable,
      HasJudgementOrLien: this.props.HasJudgementOrLien,
      JudgementOrLien: this.props.JudgementOrLien,
      ...queue,
    });
  };

  updateFederalDebtType = (values: FinancialFederalDebtItemModel['FederalDebtType']) => {
    this.update({
      FederalDebtType: values,
    });
  };

  updateFederalDebtTypeReason = (values: FinancialFederalDebtItemModel['FederalDebtTypeReason']) => {
    this.update({
      FederalDebtTypeReason: values,
    });
  };

  updateYear = (values: FinancialFederalDebtItemModel['Year']) => {
    this.update({
      Year: values,
    });
  };

  updateReason = (values: FinancialFederalDebtItemModel['Reason']) => {
    this.update({
      Reason: values,
    });
  };

  updateReasonNoActions = (values: FinancialFederalDebtItemModel['ReasonNoActions']) => {
    this.update({
      ReasonNoActions: values,
    });
  };

  updateIntent = (values: FinancialFederalDebtItemModel['Intent']) => {
    this.update({
      Intent: values,
    });
  };

  updateDate = (values: FinancialFederalDebtItemModel['Date']) => {
    this.update({
      Date: values,
    });
  };

  updateAmount = (values: FinancialFederalDebtItemModel['Amount']) => {
    this.update({
      Amount: values,
    });
  };

  updateAmountIDK = (values: FinancialFederalDebtItemModel['AmountIDK']) => {
    const queue: Partial<FinancialFederalDebtItemModel> = { AmountIDK: values };

    if (values?.applicable === false) {
      queue.Amount = {} as FieldModel<string>;
    }

    this.update(queue);
  };

  updateJudgementAmount = (values: FinancialFederalDebtItemModel['JudgementAmount']) => {
    this.update({
      JudgementAmount: values,
    });
  };

  updateActionsTaken = (values: FinancialFederalDebtItemModel['ActionsTaken']) => {
    const list = Checkbox.select(values, this.props.ActionsTaken);

    if (list.includes('None')) {
      const checkBoxGroupOnlyNone: FinancialFederalDebtItemModel['ActionsTaken'] = { values: ['None'] };
      this.update({
        ActionsTaken: checkBoxGroupOnlyNone,
        ActionsTakenReason: {} as FinancialFederalDebtItemModel['ActionsTakenReason'],
      });
    } else {
      this.update({
        ActionsTaken: { values: list },
        ActionsTakenReason: list.includes('Other') ? this.props.ActionsTakenReason : {} as FinancialFederalDebtItemModel['ActionsTakenReason'],
      });
    }
  };

  updateActionsTakenReason = (values: FinancialFederalDebtItemModel['ActionsTakenReason']) => {
    this.update({
      ActionsTakenReason: values,
    });
  };

  updateAgency = (values: FinancialFederalDebtItemModel['Agency']) => {
    this.update({
      Agency: values,
    });
  };

  updateAccountNum = (values: FinancialFederalDebtItemModel['AccountNum']) => {
    this.update({
      AccountNum: values,
    });
  };

  updatePropertyType = (values: FinancialFederalDebtItemModel['PropertyType']) => {
    this.update({
      PropertyType: values,
    });
  };

  updatePropertyTypeNotApplicable = (values: FinancialFederalDebtItemModel['PropertyTypeNotApplicable']) => {
    this.update({
      PropertyTypeNotApplicable: values,
      PropertyType: values?.applicable === false ? {} as FinancialFederalDebtItemModel['PropertyType'] : this.props.PropertyType,
    });
  };

  updateAmountEstimated = (values: FinancialFederalDebtItemModel['AmountEstimated']) => {
    this.update({
      AmountEstimated: values,
    });
  };

  updateHasJudgementOrLien = (values: FinancialFederalDebtItemModel['HasJudgementOrLien']) => {
    if (values?.value === 'No') {
      this.update({
        HasJudgementOrLien: values,
        JudgementOrLien: {} as FinancialFederalDebtItemModel['JudgementOrLien'],
        CourtName: {} as FinancialFederalDebtItemModel['CourtName'],
        CourtLocation: {} as FinancialFederalDebtItemModel['CourtLocation'],
        ActionsTaken: {} as FinancialFederalDebtItemModel['ActionsTaken'],
        ActionsTakenReason: {} as FinancialFederalDebtItemModel['ActionsTakenReason'],
      });
    } else {
      this.update({
        HasJudgementOrLien: values,
        JudgementOrLien: this.props.JudgementOrLien,
        CourtName: this.props.CourtName,
        CourtLocation: this.props.CourtLocation,
        ActionsTaken: this.props.ActionsTaken,
        ActionsTakenReason: this.props.ActionsTakenReason,
      });
    }
  };

  updateJudgementOrLien = (values: FinancialFederalDebtItemModel['JudgementOrLien']) => {
    this.update({
      JudgementOrLien: values,
    });
  };

  updateCourtName = (values: FinancialFederalDebtItemModel['CourtName']) => {
    this.update({
      CourtName: values,
    });
  };

  updateCourtLocation = (values: FinancialFederalDebtItemModel['CourtLocation']) => {
    this.update({
      CourtLocation: values,
    });
  };

  render() {
    // This is an item in a list it needs the attribute properly set
    const attribute = `${this.props.attribute}.items[${this.props.$index}].Item`;

    return (
      <>
        {this.props.activity === 'failedToFile' && (
          <>
            <Field
              title={i18n.t('financial.pvq.federalDebt.failedToFile.year')}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.Year`}
            >
              <DateControl
                name="Year"
                {...this.props.Year}
                className="federaldebt-year"
                hideMonth
                hideDay
                showEstimated={false}
                required={this.props.required}
                onUpdate={this.updateYear}
                onError={this.props.onError}
                attribute={`${attribute}.Year`}
              />
            </Field>
            <Field
              title={i18n.t('financial.pvq.federalDebt.failedToFile.reason')}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.Reason`}
            >
              <Textarea
                name="Reason"
                {...this.props.Reason}
                className="federaldebt-reason"
                required={this.props.required}
                onUpdate={this.updateReason}
                onError={this.props.onError}
                attribute={`${attribute}.Reason`}
              />
            </Field>
            <Branch
              {...this.props.Intent}
              label={i18n.t('financial.pvq.federalDebt.failedToFile.intent.question')}
              name="Intent"
              className="intent"
              onUpdate={this.updateIntent}
              required={this.props.required}
              onError={this.props.onError}
              attribute={`${attribute}.Intent`}
            />
            {this.props.Intent?.value === 'Yes' && (
              <Field
                title={i18n.t('financial.pvq.federalDebt.failedToFile.intent.ifYes')}
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.Date`}
              >
                <DateControl
                  {...this.props.Date}
                  name="date"
                  noMaxDate
                  hideDay
                  showEstimated
                  onUpdate={this.updateDate}
                  onError={this.props.onError}
                  required={this.props.required}
                  attribute={`${attribute}.Date`}
                />
              </Field>
            )}
            {this.props.Intent?.value === 'No' && (
              <Field
                title={i18n.t('financial.pvq.federalDebt.failedToFile.intent.ifNo')}
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.ReasonNoActions`}
              >
                <Textarea
                  name="IfNoIntent"
                  {...this.props.ReasonNoActions}
                  name="reasonNoActions"
                  className="federaldebt-reason-no-actions"
                  required={this.props.required}
                  onUpdate={this.updateReasonNoActions}
                  onError={this.props.onError}
                  attribute={`${attribute}.ReasonNoActions`}
                />
              </Field>
            )}
          </>
        )}

        {this.props.activity === 'failedToPay' && (
          <>
            <Field
              title={i18n.t('financial.pvq.federalDebt.failedToPay.year')}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.Year`}
            >
              <DateControl
                name="Year"
                {...this.props.Year}
                className="federaldebt-year"
                hideMonth
                hideDay
                showEstimated={false}
                minDate={this.props.Year}
                minDateEqualTo
                required={this.props.required}
                onUpdate={this.updateYear}
                onError={this.props.onError}
                attribute={`${attribute}.Year`}
              />
            </Field>
            <Field
              title={i18n.t('financial.pvq.federalDebt.failedToPay.amount')}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.Amount`}
            >
              <NotApplicable
                {...this.props.AmountIDK}
                label={i18n.t('financial.pvq.federalDebt.idkLabel')}
                name="AmountIDK"
                onUpdate={this.updateAmountIDK}
                required={this.props.required}
                attribute={`${attribute}.AmountIDK`}
              >
                <Currency
                  {...this.props.Amount}
                  name="Amount"
                  className="federaldebt-amount"
                  required={this.props.required}
                  onUpdate={this.updateAmount}
                  onError={this.props.onError}
                  attribute={`${attribute}.Amount`}
                />
              </NotApplicable>
            </Field>
            <Field
              title={i18n.t('financial.pvq.federalDebt.failedToPay.actionsTaken.question')}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.ActionsTaken`}
            >
              <CheckboxGroup
                className="actions-taken option-list option-list-vertical"
                attribute={`${attribute}.ActionsTaken`}
                onError={this.props.onError}
                required={this.props.required}
                selectedValues={(this.props.ActionsTaken || {}).values}
              >
                {taxDebtActionOptions.map((action, i) => (
                  <Checkbox
                    name="actions-taken"
                    key={action}
                    label={action}
                    value={action}
                    onUpdate={this.updateActionsTaken}
                    onError={this.props.onError}
                  />
                ))}
              </CheckboxGroup>

              {this.props.ActionsTaken && (this.props.ActionsTaken?.values?.includes('Other') || this.props.ActionsTaken?.values?.includes('None')) && (
                <Field
                  title={i18n.t('financial.pvq.federalDebt.failedToPay.actionsTaken.ifNoneOrOther')}
                  scrollIntoView={this.props.scrollIntoView}
                  attribute={`${attribute}.ActionsTakenReason`}
                >
                  <Textarea
                    {...this.props.ActionsTakenReason}
                    className="actions-taken-reason"
                    required={this.props.required}
                    onUpdate={this.updateActionsTakenReason}
                    onError={this.props.onError}
                    attribute={`${attribute}.ActionsTakenReason`}
                  />
                </Field>
              )}
            </Field>
          </>
        )}

        {this.props.activity === 'otherPastDue' && (
          <>
            <Field
              title={i18n.t('financial.pvq.federalDebt.otherPastDue.agency')}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.Agency`}
            >
              <Text
                {...this.props.Agency}
                name="Agency"
                className="taxes-agency"
                required={this.props.required}
                onUpdate={this.updateAgency}
                onError={this.props.onError}
                attribute={`${attribute}.Agency`}
              />
            </Field>

            <Field
              title={i18n.t('financial.pvq.federalDebt.otherPastDue.debtType.question')}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.FederalDebtType`}
            >
              <RadioGroup
                className="federal-debt-types option-list option-list-vertical"
                attribute={`${attribute}.FederalDebtType`}
                onError={this.props.onError}
                required={this.props.required}
                selectedValue={(this.props.FederalDebtType || {}).value}
              >
                {federalDebtTypes.map((debtType) => (
                  <Radio
                    key={debtType}
                    name="federal-debt-type"
                    label={debtType}
                    value={debtType}
                    className={debtType}
                    onUpdate={this.updateFederalDebtType}
                    onError={this.props.onError}
                    onBlur={this.props.onBlur}
                    onFocus={this.props.onFocus}
                  />
                ))}
              </RadioGroup>

              {this.props.FederalDebtType?.value === 'Other' && (
                <Field
                  title={i18n.t('financial.pvq.federalDebt.otherPastDue.debtType.ifOther')}
                  scrollIntoView={this.props.scrollIntoView}
                  attribute={`${attribute}.FederalDebtTypeReason`}
                >
                  <Textarea
                    {...this.props.FederalDebtTypeReason}
                    className="debt-type-reason"
                    required={this.props.required}
                    onUpdate={this.updateFederalDebtTypeReason}
                    onError={this.props.onError}
                    attribute={`${attribute}.FederalDebtTypeReason`}
                  />
                </Field>
              )}
            </Field>

            <Field
              title={i18n.t('financial.pvq.federalDebt.otherPastDue.accountNum')}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.AccountNum`}
            >
              <Textarea
                name="AccountNum"
                {...this.props.AccountNum}
                className="taxes-accountnum"
                required={this.props.required}
                onUpdate={this.updateAccountNum}
                onError={this.props.onError}
                attribute={`${attribute}.AccountNum`}
              />
            </Field>

            <Field
              title={i18n.t('financial.pvq.federalDebt.otherPastDue.propertyType')}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.PropertyType`}
            >
              <NotApplicable
                name="PropertyTypeNotApplicable"
                {...this.props.PropertyTypeNotApplicable}
                label={i18n.t('financial.pvq.shared.na')}
                onUpdate={this.updatePropertyTypeNotApplicable}
                attribute={`${attribute}.PropertyTypeNotApplicable`}
              >
                <Text
                  name="PropertyType"
                  value={this.props.PropertyType?.value}
                  onUpdate={this.updatePropertyType}
                  attribute={`${attribute}.PropertyType`}
                  required={this.props.required}
                />
              </NotApplicable>
            </Field>
            <Field
              title={i18n.t('financial.pvq.federalDebt.otherPastDue.amount')}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.Amount`}
            >
              <div>
                <Currency
                  name="Amount"
                  {...this.props.Amount}
                  className="taxes-amount"
                  min="1"
                  required={this.props.required}
                  onUpdate={this.updateAmount}
                  onError={this.props.onError}
                  attribute={`${attribute}.Amount`}
                />
                <div className="flags">
                  <Checkbox
                    {...this.props.AmountEstimated}
                    name="AmountEstimated"
                    label={i18n.t('financial.taxes.label.estimated')}
                    toggle="false"
                    onUpdate={this.updateAmountEstimated}
                    onError={this.props.onError}
                    attribute={`${attribute}.AmountEstimated`}
                  />
                </div>
              </div>
            </Field>

            <Field
              title={i18n.t('financial.pvq.federalDebt.otherPastDue.why')}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.Reason`}
            >
              <Textarea
                name="Reason"
                {...this.props.Reason}
                className="taxes-reason"
                required={this.props.required}
                onUpdate={this.updateReason}
                onError={this.props.onError}
                attribute={`${attribute}.Reason`}
              />
            </Field>

            <Field
              title={i18n.t('financial.pvq.federalDebt.otherPastDue.when')}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.Date`}
            >
              <DateControl
                name="Date"
                {...this.props.Date}
                className="taxes-date"
                hideDay
                showEstimated
                required={this.props.required}
                onUpdate={this.updateDate}
                onError={this.props.onError}
                attribute={`${attribute}.Date`}
              />
            </Field>

            <Branch
              name="HasJudgementOrLien"
              {...this.props.HasJudgementOrLien}
              label={i18n.t('financial.pvq.federalDebt.otherPastDue.legalState.question')}
              className="hasJudgementOrLien"
              onUpdate={this.updateHasJudgementOrLien}
              warning
              required={this.props.required}
              onError={this.props.onError}
              attribute={`${attribute}.HasJudgementOrLien`}
            />

            {this.props.HasJudgementOrLien?.value === 'Yes'
              && (
                <>
                  <Field
                    title={i18n.t('financial.pvq.federalDebt.otherPastDue.legalState.ifYes.question')}
                    scrollIntoView={this.props.scrollIntoView}
                    attribute={`${attribute}.JudgementOrLien`}
                  >
                    <RadioGroup
                      name="JudgementOrLien"
                      {...this.props.JudgementOrLien}
                      className="judgementOrLien"
                      selectedValue={(this.props.JudgementOrLien || {}).value}
                      required={this.props.required}
                      onError={this.props.onError}
                      attribute={`${attribute}.JudgementOrLien`}
                    >
                      <Radio
                        className="JudgementOrLien"
                        label={i18n.t('financial.pvq.federalDebt.otherPastDue.legalState.ifYes.judgement')}
                        value="Judgement"
                        onUpdate={this.updateJudgementOrLien}
                        onError={this.props.onError}
                        onBlur={this.props.onBlur}
                        onFocus={this.props.onFocus}
                      />
                      <Radio
                        className="JudgementOrLien"
                        label={i18n.t('financial.pvq.federalDebt.otherPastDue.legalState.ifYes.lien')}
                        value="Lien"
                        onUpdate={this.updateJudgementOrLien}
                        onError={this.props.onError}
                        onBlur={this.props.onBlur}
                        onFocus={this.props.onFocus}
                      />
                    </RadioGroup>
                  </Field>

                  <Field
                    title={i18n.t('financial.pvq.federalDebt.otherPastDue.legalState.ifYes.courtName')}
                    scrollIntoView={this.props.scrollIntoView}
                    attribute={`${attribute}.CourtName`}
                  >
                    <Text
                      name="CourtName"
                      {...this.props.CourtName}
                      className="court-name"
                      required={this.props.required}
                      onUpdate={this.updateCourtName}
                      onError={this.props.onError}
                      attribute={`${attribute}.CourtName`}
                    />
                  </Field>

                  <Field
                    title={i18n.t('financial.pvq.federalDebt.otherPastDue.legalState.ifYes.courtAddress')}
                    attribute={`${attribute}.CourtLocation`}
                    titleSize="h4"
                    adjustFor="address"
                    scrollIntoView={this.props.scrollIntoView}
                  >
                    <PVQLocation
                      {...this.props.CourtLocation}
                      attribute={`${attribute}.CourtLocation`}
                      name="CourtLocation"
                      onError={this.props.onError}
                      onUpdate={this.updateCourtLocation}
                      layout={Layouts.US_STREET_CITY_STATE_ZIP}
                      required={this.props.required}
                      inUSA
                      geocode
                      dispatch={this.props.dispatch}
                      addressBooks={this.props.addressBooks}
                      addressBook="Court"
                    />
                  </Field>

                  <Field
                    title={i18n.t('financial.pvq.federalDebt.otherPastDue.legalState.ifYes.amount')}
                    scrollIntoView={this.props.scrollIntoView}
                    attribute={`${attribute}.JudgementAmount`}
                  >
                    <Currency
                      {...this.props.JudgementAmount}
                      name="JudgementAmount"
                      className="taxes-judgement-amount"
                      min="1"
                      required={this.props.required}
                      onUpdate={this.updateJudgementAmount}
                      onError={this.props.onError}
                      attribute={`${attribute}.JudgementAmount`}
                    />
                  </Field>

                  <Field
                    title={i18n.t('financial.pvq.federalDebt.otherPastDue.actionsTaken.question')}
                    scrollIntoView={this.props.scrollIntoView}
                    attribute={`${attribute}.ActionsTaken`}
                  >
                    <CheckboxGroup
                      className="actions-taken option-list option-list-vertical"
                      attribute={`${attribute}.ActionsTaken`}
                      onError={this.props.onError}
                      required={this.props.required}
                      selectedValues={(this.props.ActionsTaken || {}).values}
                    >
                      {nonTaxDebtActionOptions.map((action, i) => (
                        <Checkbox
                          key={action}
                          name="actions-taken"
                          label={action}
                          value={action}
                          className={action}
                          onUpdate={this.updateActionsTaken}
                          onError={this.props.onError}
                        />
                      ))}
                    </CheckboxGroup>

                    {(this.props.ActionsTaken?.values?.includes('Other') || this.props.ActionsTaken?.values?.includes('None')) && (
                      <Field
                        title={i18n.t('financial.pvq.federalDebt.otherPastDue.actionsTaken.ifNoneOrOther')}
                        scrollIntoView={this.props.scrollIntoView}
                        attribute={`${attribute}.ActionsTakenReason`}
                      >
                        <Textarea
                          {...this.props.ActionsTakenReason}
                          className="actions-taken-reason"
                          required={this.props.required}
                          onUpdate={this.updateActionsTakenReason}
                          onError={this.props.onError}
                          attribute={`${attribute}.ActionsTakenReason`}
                        />
                      </Field>
                    )}
                  </Field>
                </>
              )}
          </>
        )}
      </>
    );
  }
}
